import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import * as notify from "shared/notify";
import { useNavigate } from "react-router-dom";
import TaskNav from "components/task-nav/task-nav";
import Modal from "components/modal";
import { CompareImages } from "components/product-match/components/compare-images";
import { ZoomCompareImages } from "components/product-match/components/zoom-compare-images";
import { useLastOpenedDuplicate } from "./hooks/use-last-opened-duplicate";
import { useGetPotentialMatches } from "./hooks/use-get-potential-matches";
import { useGetImageCompareAttributeGroups } from "./hooks/use-get-image-compare-attribute-groups";
import { useSaveMatchType } from "./hooks/use-save-match-type";
import { SHIPMENT_TYPE_MAP } from "components/shipments/lib/constants";
import { ProductCompareHeader } from "../components/product-compare-header";
import { ProductCompareAttributes } from "../components/product-compare-attributes";
import { TaskContext } from "contexts/task";
import { MATCH_TYPES } from "../lib/constants";
import { PRODUCT_MATCH_STEPS } from "components/product-match/lib/constants";
import { useDuplicateMatch } from "../../../shared/hooks/api/use-duplicate-match";
import Loader from "components/loader/loader";
import NotificationBar from "../../notification-bar";
import { SettingsContext } from "../../../contexts/settings";
import { cesFeatureGuids } from "../../../contexts/settings/lib/constants";

const ProductCompareIndex = () => {
    const { task } = useContext(TaskContext);
    const { settings } = useContext(SettingsContext);
    const isDigitalOrDigitalLite = task.context.shipmentType === 1 || task.context.shipmentType === 2;
    const isPhysicalRemote = task.context.shipmentProductFeatures.find(
        (feature) =>
            feature.code === settings.intakeConfigFeatures[cesFeatureGuids.PHYREMSTPRODUCT]?.code &&
            settings.intakeConfigFeatures[cesFeatureGuids.PHYREMSTPRODUCT]?.isActive
    );
    const { mutate: postLastVisitedDuplicate } = useLastOpenedDuplicate();
    const { mutate: postMatchType } = useSaveMatchType();
    const navigate = useNavigate();

    const DEFAULT_PRODUCT_COMPARE = { upc: "", modifier: "", taskContextKey: "", productId: "", images: [] };
    const { data: potentialMatches, isSuccess } = useGetPotentialMatches(task.taskContextKey);
    const { data: imageCompareAttributeGroups, isSuccess: attributesLoaded } = useGetImageCompareAttributeGroups();
    const { data: references } = useDuplicateMatch(task.taskContextKey, isDigitalOrDigitalLite || !!isPhysicalRemote);
    const [step, setStep] = useState(PRODUCT_MATCH_STEPS.compare);
    const [productCompare, setProductCompare] = useState(DEFAULT_PRODUCT_COMPARE);
    const [columnWidths, setColumnWidth] = useState({});
    const [productMatches, setProductMatches] = useState([]);
    const [imageAttributeGroups, setImageAttributeGroups] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const handleInitialColSizes = useCallback(() => setColumnWidth({}), []);
    const isCostcoConfigActive = settings.intakeConfigFeatures[cesFeatureGuids.COSTCOSPSUP]?.isActive;
    const productFeature = useMemo(
        () => task.context.shipmentProductFeatures?.find((product) => product.id === cesFeatureGuids.COSTCOSPSUP),
        [task.context.shipmentProductFeatures]
    );

    useEffect(() => {
        // Set Last Opened
        if (task.key) {
            postLastVisitedDuplicate(task.key);
        }
    }, [task, postLastVisitedDuplicate]);

    useEffect(() => {
        if (potentialMatches && isSuccess) {
            setProductMatches(potentialMatches);
            handleInitialColSizes();
        }
    }, [potentialMatches, isSuccess, handleInitialColSizes, setProductMatches]);

    useEffect(() => {
        if (imageCompareAttributeGroups && attributesLoaded) {
            setImageAttributeGroups(imageCompareAttributeGroups);
        }
    }, [imageCompareAttributeGroups, attributesLoaded]);

    const navigateBack = () => {
        navigate("/duplicate-staging/");
    };

    const handleColumnWidthChange = useCallback((col, width) => {
        setColumnWidth((state) => ({ ...state, [col]: width }));
    }, []);

    const handleStep = useCallback((name) => {
        //Handles 'Back' button on IMAGE_COMPARE. Set to close modal since PRODUCT_MATCH doesn't exist.
        if (name === PRODUCT_MATCH_STEPS.match) {
            return closeModal();
        }

        setStep(name);
    }, []);

    const handleZoomCompare = useCallback(
        (selectedKey) => {
            setStep(PRODUCT_MATCH_STEPS.zoom);

            const selectedMatch = productMatches.find(({ taskContextKey }) => taskContextKey === selectedKey);

            if (!selectedMatch) {
                return;
            }

            setProductCompare(selectedMatch);
        },
        [productMatches]
    );

    const openModal = () => {
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setStep(PRODUCT_MATCH_STEPS.compare);
    };

    const handleMatchSelection = async (match, matchesTaskContextKey, isProductFinalized) => {
        const confirm = await notify.confirm(
            `${match.label} cannot be undone. ${!isProductFinalized && match === MATCH_TYPES.exactMatch ? match.wipPrompt : match.prompt}`
        );

        if (!confirm) {
            return;
        }

        postMatchType(
            { taskContextKey: task.taskContextKey, matchType: match.value, matchesTaskContextKey },
            {
                onSuccess: async (data) => {
                    if (data && data.success === false) {
                        return notify.error("Product did not save.");
                    }

                    notify.success("Saved!");
                    navigateBack();
                },
            }
        );
    };

    if (!task) {
        return (
            <div className="txt-center">
                <Loader natural={true} />
            </div>
        );
    }

    return (
        <div className="container flex-1 -side-scroll">
            <TaskNav>
                <div className="button-group p-t-12 p-b-24 p-l-30">
                    <button
                        className="button"
                        onClick={navigateBack}
                    >
                        Back
                    </button>
                    <h1 className="txt-center">
                        Duplicate Staging - {task.fullUpc}
                        {task.context.shipmentType >= 0 && <span className="ml-1 mr-1">({SHIPMENT_TYPE_MAP[task.context.shipmentType].label})</span>}
                    </h1>
                    <button
                        className="button -primary -no-margin"
                        onClick={() => handleMatchSelection(MATCH_TYPES.noMatch)}
                    >
                        No Match
                    </button>
                </div>
            </TaskNav>
            <Modal
                show={showModal}
                onClose={closeModal}
            >
                {step === PRODUCT_MATCH_STEPS.compare && (
                    <CompareImages
                        upc={task.upc}
                        onStepChange={handleStep}
                        reference={references[0]}
                        matchedProducts={productMatches}
                        onZoomCompare={handleZoomCompare}
                        imageAttributeGroups={imageAttributeGroups}
                        isPhysicalRemote={isPhysicalRemote}
                    />
                )}
                {step === PRODUCT_MATCH_STEPS.zoom && (
                    <ZoomCompareImages
                        productShots={references[0] && references[0].images}
                        intakeProductId={references[0] && references[0].photographyWipKey}
                        onStepChange={handleStep}
                        reference={references[0]}
                        productMatches={productMatches}
                        selectedProductCompare={productCompare}
                        onZoomCompare={handleZoomCompare}
                        showSingleDisplay={!references[0]}
                        isDuplicateStaging
                        isPhysicalRemote={isPhysicalRemote}
                    />
                )}
            </Modal>

            <div className="flex-column align-stretch fit-content">
                <div className="m-l-30">
                    {isCostcoConfigActive && productFeature && (
                        <NotificationBar
                            variant="small"
                            type="info"
                        >
                            {productFeature.name}
                        </NotificationBar>
                    )}
                </div>
                <ProductCompareHeader
                    task={task}
                    columnWidths={columnWidths}
                    onColumnWidthChange={handleColumnWidthChange}
                    onInitColSizes={handleInitialColSizes}
                    productMatches={productMatches}
                    references={references}
                    openModal={openModal}
                    onMatchSelection={handleMatchSelection}
                />
                <ProductCompareAttributes
                    productMatches={productMatches}
                    references={references}
                    columnWidths={columnWidths}
                />
            </div>
        </div>
    );
};

export default ProductCompareIndex;
